import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import { useOnClickOutside, useToggle } from "usehooks-ts";

import Button from "components/Button";
import Input from "components/Input";
import { CARRIER_PAYMENT_SPEED } from "constants/haulpay-carrier";
import { routes } from "constants/routes";
import FactoringCompany from "features/main/HaulPay/Shared/FactoringCompany";
import { showErrorMessage } from "helpers/toastMessage";
import useDebounce from "hooks/useDebounce";
import useDeviceType from "hooks/useDeviceType";
import { IOption } from "models/Form";
import { IFactoringCompany, IPaymentProfile } from "models/HaulPay";
import {
  getFactoringCompanyAPI,
  sendInviteToFactoringCompanyAPI,
  setFactoringCompanyRelationAPI,
  updateCarrierProfileAPI
} from "services/api/haulpay";
import { selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";
import PaymentSpeedInput from "components/PaymentSpeedInput";
import { useAnalytics } from "hooks/useAnalytics";

interface ISetupPaymentSpeedForm {
  payment_speed: IOption;
  factoring_company: IFactoringCompany;
}

type SetupPaymentSpeedProps = {
  type: "existing_profile" | "create_new_profile" | "invited_profile";
  paymentProfile?: IPaymentProfile;
  handleNext: VoidFunction;
};

export default function SetupPaymentSpeed({
  type,
  paymentProfile,
  handleNext
}: Readonly<SetupPaymentSpeedProps>) {
  const navigate = useNavigate();
  const { isDesktop } = useDeviceType();
  const [isDirectPay, toggleIsDirectPay] = useToggle(true);
  const [isSearchCompany, setIsSearchCompany] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [showCompanyList, setShowCompanyList] = useState(false);
  const [factoringCompanies, setFactoringCompanies] = useState<IFactoringCompany[]>([]);
  const [companyFilterString, setCompanyFilterString] = useState("");
  const debouncedSearchString = useDebounce(companyFilterString, 500);
  const searchModalRef = useRef<HTMLInputElement>(null);
  const userDetails = useSelector(selectUserDetails);
  const { trackAllEvents } = useAnalytics();

  const {
    control,
    setValue,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<Partial<ISetupPaymentSpeedForm>>({
    defaultValues: {
      payment_speed: CARRIER_PAYMENT_SPEED[0]
    }
  });

  function goBack() {
    navigate(routes.HAULPAY_BROKER_CARRIERS_NEW);
  }

  const getFactoringCompany = async (name: string) => {
    setIsSearchCompany(true);
    const { data: factoringCompanyResult } = await getFactoringCompanyAPI(name);
    setFactoringCompanies(factoringCompanyResult.results);
    setIsSearchCompany(false);
  };

  const handleClickCompany = (company: IFactoringCompany) => {
    setValue("factoring_company", company);
    setShowCompanyList(false);
    setCompanyFilterString(company.name);
  };

  const onSubmit = async (data: ISetupPaymentSpeedForm) => {
    setIsSendingInvite(true);
    try {
      if (isDirectPay) {
        await updateCarrierProfileAPI({
          factoring_id: userDetails.factoring_id,
          carrier_id: paymentProfile?.relationship_id ?? "",
          data: {
            payout_days: data.payment_speed?.value
          }
        });
        trackAllEvents("add_carrier_broker_relation_set_payment_speed_page1");
      } else {
        await setFactoringCompanyRelationAPI(
          data.factoring_company.id,
          userDetails.factoring_id,
          paymentProfile?.id ?? ""
        );
        await sendInviteToFactoringCompanyAPI(data.factoring_company.id, paymentProfile?.id ?? "");
        trackAllEvents("add_carrier_broker_relation_set_factoring_company_page1");
      }
      trackAllEvents("add_carrier_broker_relation");
      handleNext();
    } catch (err) {
      showErrorMessage("Error setting up payment speed");
    } finally {
      setIsSendingInvite(false);
    }
  };

  useOnClickOutside([searchModalRef], () => {
    setShowCompanyList(false);
  });

  useEffect(() => {
    getFactoringCompany(debouncedSearchString);
  }, [debouncedSearchString]);

  return (
    <form className={styles.setupPaymentContainer} onSubmit={handleSubmit(onSubmit)}>
      <h3 className={styles.title}>
        {type === "invited_profile" ? "Create Carrier payment Profile" : "Add a Carrier"}
      </h3>
      <div className={styles.body}>
        {type === "invited_profile" ? (
          <span>
            If the carrier has not been able to complete the boarding themselves this enables you to
            create their payment profile for them to be reviewed.
          </span>
        ) : (
          <>
            <span>An existing profile matches the email address you’ve entered.</span>
            <span>
              If you want to pay the carrier directly, please select a payment speed below and
              upload a voided cheque or acceptable bank docs to ensure we have the correct
              information.
            </span>
            <span>
              Please note: If the carrier does not have direct payment info on file and is set up
              with a factoring company, we will attempt to gather and confirm direct pay info with
              the carrier.
            </span>
            <div>
              <span>
                If you want to pay the carrier’s factoring company, please choose “Select a
                factoring company” and upload the carrier’s NOA.
              </span>
              <ul className={styles.text}>
                <li>
                  If you can’t find the correct factoring company, please enter and select “Not
                  Found” and upload NOA and our team will take it from there.
                </li>
                <li>
                  <span>
                    If you choose “Not Found” and don’t have an NOA or get error saying the carrier
                    is connected to another factor, please email us at{" "}
                  </span>
                  <a href="mailto:invitedcarriers@haulpay.com" className="underline">
                    invitedcarriers@haulpay.com
                  </a>
                  <span>
                    {" "}
                    with the Carrier’s Name, Email, MC#, NOA (If available) and the name of factor
                    you want to pay.
                  </span>
                </li>
              </ul>
            </div>

            <span>
              WARNING: If you don’t complete this process, this carrier will be added to your
              carrier section with One Day quick pay with your default broker option.
            </span>

            <div className="pt-4">
              <span>Any questions or concerns please email us at </span>
              <a href="mailto:invitedcarriers@haulpay.com" className="underline">
                invitedcarriers@haulpay.com
              </a>
            </div>
          </>
        )}

        <div className={styles.select}>
          {isDirectPay ? (
            <PaymentSpeedInput
              noLabel
              control={control}
              name="payment_speed"
              inputProps={{ className: "min-w-fit w-full desktop:w-fit" }}
            />
          ) : (
            <div ref={searchModalRef} className="relative w-full">
              <Input
                type="text"
                isLoading={isSearchCompany}
                value={companyFilterString}
                placeholder={isDirectPay ? "" : "Click here to search company."}
                onChange={(event) => setCompanyFilterString(event.target.value)}
                onFocus={() => setShowCompanyList(true)}
              />
              {showCompanyList ? (
                <div className="absolute left-0 flex flex-col w-full overflow-y-auto rounded-md top-14 bg-input max-h-48">
                  {factoringCompanies.map((factoringCompany) => (
                    <FactoringCompany
                      key={factoringCompany.id}
                      factoring_company={factoringCompany}
                      onClickCompany={() => handleClickCompany(factoringCompany)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          )}
          <button
            type="button"
            className={styles.selectFactoringCompanyBtn}
            onClick={toggleIsDirectPay}>
            {isDirectPay ? "Select a Factoring Company" : "Select a Payment Speed"}
          </button>
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          type="button"
          variant="outline"
          rounded
          size="lg"
          onClick={goBack}
          className="desktop:!rounded-base desktop:!w-48">
          {isDesktop ? "Cancel" : "CANCEL"}
        </Button>
        <Button
          type="submit"
          disabled={isSendingInvite || isSubmitting}
          rounded
          size="lg"
          className="desktop:!rounded-base desktop:!w-48">
          {isSendingInvite || isSubmitting ? (
            <Icon path={mdiLoading} size={1.5} spin={1} />
          ) : isDesktop ? (
            "Next"
          ) : (
            "NEXT"
          )}
        </Button>
      </div>
    </form>
  );
}
